import '@fullstory/typeutils';
import type { ClientMessage, IFrameMessage } from './messages';

// Register a service worker for the given scope (which doesn't need to include
// the current document). Returns a promise that resolves when the new service
// worker is either active or has been made redundant by a newer active service
// worker.
export async function registerServiceWorker(
  workerHref: string,
  scope?: string,
): Promise<ServiceWorkerRegistration> {
  if (!navigator.serviceWorker) {
    throw new Error(`The service worker API is unavailable`);
  }

  const registration = await navigator.serviceWorker.register(workerHref, { scope });
  console.log(`Service worker registration succeeded for scope '${scope ? scope : 'default'}'.`);

  // Trigger an update if a (possibly out-of-date) service worker is already
  // active. (When there's no active service worker, update() throws.)
  const existingServiceWorker = registration.active;
  if (existingServiceWorker) {
    await registration.update();
  }
  console.log(`Service worker updated.`);

  if (existingServiceWorker) {
    return registration;
  }

  const serviceWorkerReady = new Promise<void>(resolve => {
    // Find the newest service worker - in other words, the one which is at the
    // earliest stage of the life cycle. That should be the one we just
    // registered. Because service worker installation happens in parallel, it
    // may already be active.
    const newServiceWorker = registration.installing || registration.waiting || registration.active;
    if (!newServiceWorker) {
      throw new Error(`No pending or active service worker`);
    }

    // Wait for the new service worker to be either activated, in which case
    // it's ready for use, or redundant, which may happen if an even newer
    // service worker gets loaded in parallel in another tab. The latter isn't a
    // problem; the main thing we want to ensure is that *some* service worker
    // is active and it's at least as new as the one we just registered.
    const onStateChange = () => {
      console.debug(`New service worker is now ${newServiceWorker.state}.`);
      switch (newServiceWorker.state) {
        case 'activated':
        case 'redundant':
          newServiceWorker.removeEventListener('statechange', onStateChange);
          resolve();
          break;
      }
    };
    newServiceWorker.addEventListener('statechange', onStateChange);

    // Check the initial state.
    onStateChange();
  });

  await serviceWorkerReady;
  return registration;
}

// Register a service worker and then notify the outer page that it's ready.
// This function implements the half of the registerCrossOriginServiceWorker()
// protocol that runs inside the cross-domain iframe; it should be called from a
// top-level, synchronous script in that iframe. See `client.ts` for the other
// half of the protocol.
export function registerServiceWorkerAndNotifyOuterDocument(workerHref: string, scope: string) {
  // Wait for an 'init' message from the outer document. The message will contain
  // a MessagePort that we'll use to notify the outer document when the service
  // worker is ready.
  const portReady = new Promise<MessagePort>(resolve => {
    window.addEventListener('message', (message: MessageEvent<ClientMessage>) => {
      if (message.data !== 'init') {
        console.debug(
          `Received unexpected message during service worker registration: ${message.data}`,
        );
        return;
      }

      const parentPort = message.ports[0];
      if (!parentPort) {
        throw new Error(`Expected to receive message port`);
      }

      resolve(parentPort);
    });
  });

  // Register the service worker. If the service worker is already running, this
  // will update it to the latest version. It's important that the scope doesn't
  // include this page! Otherwise, a bug in the service worker could prevent
  // this page from loading and make it difficult to deliver updates.
  const currentPath = window.location.pathname;
  if (currentPath.startsWith(scope)) {
    throw new Error(
      `Refusing to register service worker with scope '${scope}', ` +
        `which includes the registering document ${currentPath}`,
    );
  }

  const workerReady = registerServiceWorker(workerHref, scope);

  const storageAccessCheck =
    (document.hasStorageAccess && document.hasStorageAccess()) ?? Promise.resolve(true);

  // Once all the pieces fall into place, notify the outer document.
  Promise.allSettled([portReady, workerReady, storageAccessCheck]).then(
    ([portResult, workerResult, storageAccessCheckResult]) => {
      if (portResult.status === 'rejected') {
        console.debug('Service worker installation failed.');
        throw new Error('Unable to register port to outer document');
      }

      if (workerResult.status === 'rejected') {
        console.debug('Service worker installation failed.');
        const granted =
          storageAccessCheckResult.status === 'fulfilled' ? storageAccessCheckResult.value : true;
        // NOTE: this typed const helps ensure message type safety
        const errorMessage: IFrameMessage = granted ? 'unknownError' : 'storageAccessError';
        portResult.value.postMessage(errorMessage);
        return;
      }

      console.debug('Service worker installed.');
      // NOTE: this typed const helps ensure message type safety
      const message: IFrameMessage = 'ready';
      portResult.value.postMessage(message);
    },
  );
}
